import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, LifeBuoy, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

export const SupportButton = ({ label = "Support", className = "", testId = "support-button" }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("pending");
    setError("");
    try {
      await api.post("/support", { name, email, message });
      setState("sent");
    } catch (err) {
      setState("idle");
      setError(err.response?.data?.detail || "Could not send. Please try again.");
    }
  };

  return (
    <>
      <button
        data-testid={testId}
        onClick={() => { setOpen(true); setState("idle"); }}
        className={`font-mono2 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-[#FF3B00] transition-colors ${className}`}
      >
        {label}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center px-6 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="support-modal"
          >
            <motion.div
              className="w-full max-w-md border border-white/15 bg-[#0A0A0A] p-8"
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <LifeBuoy className="w-5 h-5 text-[#FF3B00]" />
                  <h3 className="font-display uppercase tracking-tight text-2xl">Support</h3>
                </div>
                <button onClick={() => setOpen(false)} data-testid="support-close" className="text-white/40 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {state === "sent" ? (
                <div data-testid="support-sent">
                  <CheckCircle2 className="w-8 h-8 text-[#00FF41] mb-4" />
                  <p className="text-sm text-white/70 leading-relaxed">
                    Message sent. You'll get a reply at <span className="text-white">{email}</span>.
                  </p>
                  <button onClick={() => setOpen(false)} className="btn-ghost mt-6" data-testid="support-done">
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="support-form">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name (optional)"
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
                    data-testid="support-name"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="w-full bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
                    data-testid="support-email"
                  />
                  <textarea
                    required
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe the issue…"
                    className="w-full bg-transparent border border-white/25 focus:border-[#FF3B00] outline-none p-3 font-mono2 text-sm placeholder:text-white/25 transition-colors resize-none"
                    data-testid="support-message"
                  />
                  {error && <p className="font-mono2 text-xs text-[#FF3B00]" data-testid="support-error">{error}</p>}
                  <button type="submit" disabled={state === "pending"} className="btn-forge w-full" data-testid="support-submit">
                    {state === "pending" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}
                  </button>
                  <p className="font-mono2 text-[11px] text-white/35">Replies come from cncroutersoft@gmail.com</p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
